import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import api, { TOKEN_KEY } from '../api/client';

import AdminLayout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Enquiries from './pages/Enquiries';
import Applications from './pages/Applications';
import Subscribers from './pages/Subscribers';
import Traffic from './pages/Traffic';
import Tracks from './pages/Tracks';
import Settings from './pages/Settings';

import './admin.css';

const USER_KEY = 'sp_admin_user';

/* ===========================================================================
   Auth
   =========================================================================== */
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AdminRoutes');
  return ctx;
};

function AuthProvider({ children }) {
  // Read the cached user synchronously so a refresh does not flash the login
  // screen before /auth/me comes back.
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; } catch { return null; }
  });
  const [isChecking, setIsChecking] = useState(Boolean(localStorage.getItem(TOKEN_KEY)));

  // Confirm the cached session is still valid on the server.
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) { setIsChecking(false); return; }

    api.get('/api/admin/auth/me')
      .then(({ data }) => {
        setUser(data.data);
        localStorage.setItem(USER_KEY, JSON.stringify(data.data));
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      })
      .finally(() => setIsChecking(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/api/admin/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.data.user));
    setUser(data.data.user);
    return data.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/api/admin/auth/logout'); } catch { /* sign out locally anyway */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    isChecking,
    login,
    logout,
    canWrite: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperadmin: user?.role === 'superadmin',
  }), [user, isChecking, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ===========================================================================
   Guard
   =========================================================================== */
function RequireAuth({ children }) {
  const { user, isChecking } = useAuth();
  const location = useLocation();

  if (isChecking) {
    return (
      <div className="spa-login">
        <div className="spa-login__card" style={{ textAlign: 'center' }}>
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '1.4rem', color: '#667085' }} />
          <p className="spa-login__sub" style={{ margin: '14px 0 0' }}>Checking your session…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Remember where they were headed so login can send them back.
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  return children;
}

/* ===========================================================================
   Routes
   =========================================================================== */
export default function AdminRoutes() {
  return (
    <AuthProvider>
      <div className="spa-root">
        <Routes>
          <Route path="login" element={<Login />} />

          <Route
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="enquiries" element={<Enquiries />} />
            <Route path="applications" element={<Applications />} />
            <Route path="subscribers" element={<Subscribers />} />
            <Route path="traffic" element={<Traffic />} />
            <Route path="tracks" element={<Tracks />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </AuthProvider>
  );
}
